import type { ComponentType } from "react";
import {
  Upload,
  Eye,
  Users,
  Star,
  Send,
  Bell,
  Sun,
  Layers,
  ShoppingBag,
  Workflow,
  CreditCard,
  ShieldCheck,
  Settings as SettingsIcon,
  Truck,
  KeyRound,
  Sparkles,
  Zap,
  FileText,
  BookOpen,
} from "lucide-react";

export type HelpTopic = {
  title: string;
  icon: ComponentType<{ className?: string }>;
  description: string;
  bullets: string[];
};

export const customerTopics: HelpTopic[] = [
  {
    title: "Browsing the shop",
    icon: ShoppingBag,
    description: "Find the right product for your job and check live trade pricing.",
    bullets: [
      "Open Shop from the sidebar to see all live products with current trade pricing.",
      "Use product cards to view variants, lead times, and minimum order details.",
      "Add items to your cart by metres and quantity — pricing updates as you go.",
    ],
  },
  {
    title: "Submitting jobs",
    icon: Upload,
    description: "Create new jobs by uploading artwork or building a gang sheet.",
    bullets: [
      "Click 'New job' or 'Add to cart' to start.",
      "Upload artwork (PNG, PDF, AI, EPS, or PSD) — multiple files per job allowed.",
      "For gang sheet products use 'Build gang sheet now' to lay out artwork visually.",
      "Set product, size, quantity, colours, and due date, then submit for review.",
    ],
  },
  {
    title: "Gang sheet builder",
    icon: Layers,
    description: "Lay out multiple designs on one sheet to maximise material and lower cost.",
    bullets: [
      "Open the builder from a gang sheet product or 'Build gang sheet now' on a job.",
      "Drag artwork onto the sheet — auto-resize keeps DPI safe and warns on low resolution.",
      "Use flip, duplicate, and nudge to pack designs tightly; the canvas snaps to spacing guides.",
      "Background detection flags white boxes around PNGs so you can clean them up before printing.",
      "Save your sheet to attach the preview to the job — staff use the saved layout for production.",
      "Re-opening the builder will warn before overwriting an existing saved sheet so you can keep a version.",
    ],
  },
  {
    title: "Tracking & favourites",
    icon: Eye,
    description: "Follow every job through production and pin the ones you reorder.",
    bullets: [
      "Status flow: Artwork Check → Gang Sheet → Queued → Printing → QC → Ready to Dispatch.",
      "Star any job to add it to Favourites on your dashboard for quick reorder.",
      "Open a job to see proofs, files, activity history, and add notes for the team.",
    ],
  },
  {
    title: "Cart & checkout",
    icon: CreditCard,
    description: "Review your basket, choose shipping, and place your order.",
    bullets: [
      "The cart drawer shows running totals, metres, and per-line pricing.",
      "Choose a shipping method at checkout — global rates or per-product overrides.",
      "Confirm to convert your cart into one or more jobs in your dashboard.",
    ],
  },
  {
    title: "Team collaboration",
    icon: Users,
    description: "Invite teammates to submit and track jobs together.",
    bullets: [
      "Settings → Teams: invite members by email.",
      "Team members can submit and view jobs under your company account.",
      "Manage who can change settings or place orders from the Teams panel.",
    ],
  },
  {
    title: "Notifications & email",
    icon: Bell,
    description: "Choose how and when FlightDeck contacts you.",
    bullets: [
      "Settings → Notifications controls in-app and email alerts per event type.",
      "Set urgency and digest frequency to avoid notification overload.",
      "Critical events (job on hold, ready to dispatch) always notify immediately.",
    ],
  },
  {
    title: "Appearance & accessibility",
    icon: Sun,
    description: "Tune the look and feel to match your environment.",
    bullets: [
      "Settings → Appearance: switch between Light, Dark, and System themes.",
      "System mode follows your device automatically.",
      "Reduced-motion preference is respected across animations.",
    ],
  },
  {
    title: "Account & security",
    icon: KeyRound,
    description: "Keep your account safe and up to date.",
    bullets: [
      "Settings → Account: change name, email, password, and avatar.",
      "Enable two-factor authentication (2FA) for added protection.",
      "Manage personal info and connected sessions.",
      "Cookie preferences can be reviewed any time from the Cookie policy page.",
    ],
  },
];

export const staffTopics: HelpTopic[] = [
  {
    title: "Daily workflow",
    icon: Workflow,
    description: "Run the day from Today, the Jobs Board, and the Job detail.",
    bullets: [
      "Today: see what's due, what's overdue, and your favourites.",
      "Jobs Board: drag-and-drop status changes, filter by assignee or product.",
      "Job detail: change status, payment status, assignee, and notes.",
      "Jobs table: switch between Jobs and Orders views — sort and view choice are saved per user.",
    ],
  },
  {
    title: "Draft, preview, publish",
    icon: Send,
    description: "Edit job fields safely with a preview before changes go live.",
    bullets: [
      "Edits are buffered as a draft until you press Publish.",
      "Use Preview to see a from → to diff and validation errors.",
      "Guardrails block risky transitions (e.g. Completed without QC pass).",
    ],
  },
  {
    title: "View as customer",
    icon: Eye,
    description: "Verify what a customer actually sees before publishing changes.",
    bullets: [
      "Sidebar → 'View as customer' opens the customer portal as you.",
      "An amber banner stays visible the whole time — press Esc to exit.",
      "Use this to QA shop content, themes, and announcement bars.",
    ],
  },
  {
    title: "Favourites for staff",
    icon: Star,
    description: "Pin priority jobs so they surface on Today and the Jobs Board.",
    bullets: [
      "Star a job from the board, detail page, or Today view.",
      "Favourites appear in the amber Favourites strip on Today.",
      "Each staff member has their own private favourites.",
    ],
  },
  {
    title: "Gang sheet builder",
    icon: Layers,
    description: "Open the DripApps builder to lay out and edit gang sheets.",
    bullets: [
      "Settings → Integrations: enable Drip and set DRIP_SHOP_ID.",
      "Use 'Open builder (test)' to confirm the script loads.",
      "On a job with a design ID, use 'Open builder' from the job detail.",
    ],
  },
  {
    title: "Products, shipping & cutting",
    icon: Truck,
    description: "Maintain the catalogue and pricing rules.",
    bullets: [
      "Settings → Products & Pricing: add products, variants, ribbons, and gallery images.",
      "Cutting tiers price by quantity — set min/max items per tier.",
      "Shipping methods are global by default but can be overridden per product.",
      "Gang sheet sizes can be drag-reordered per product code to control the builder sequence.",
    ],
  },
  {
    title: "Email & notifications",
    icon: Bell,
    description: "Configure SMTP, transactional templates, and digest behaviour.",
    bullets: [
      "Settings → Email: SMTP host, from address, TLS toggle.",
      "Edit per-event templates with merge variables like {{job_id}} and {{customer_name}}.",
      "Customers control their own delivery cadence in their notification settings.",
    ],
  },
  {
    title: "Admin & roles",
    icon: ShieldCheck,
    description: "User access is governed by roles in the database, not the UI.",
    bullets: [
      "Roles: customer, staff, admin — stored in profiles.role.",
      "Admins can update any profile; staff have full read/write on operational tables.",
      "RLS policies enforce access on every table — never store roles client-side.",
    ],
  },
];

export const docsTopics: HelpTopic[] = [
  {
    title: "Architecture",
    icon: FileText,
    description: "TanStack Start v1, Vite 7, React 19, Supabase (Lovable Cloud).",
    bullets: [
      "File-based routing under src/routes/ with __root.tsx as the shell.",
      "Server functions via createServerFn + auth middleware in src/integrations/supabase.",
      "Data layer wraps Supabase clients (browser, auth, admin server-only).",
    ],
  },
  {
    title: "DripApps integration",
    icon: Zap,
    description: "Gang sheet builder script + per-product sizes.",
    bullets: [
      "Script loaded on demand by useGangSheetBuilderScript.",
      "Sizes pulled from the gangsheet_sizes table, filtered by product_code.",
      "Status surfaced in Settings → Integrations (checking, ready, error, …).",
    ],
  },
  {
    title: "Notifications & email",
    icon: Sparkles,
    description: "Per-user preferences, in-app + email transports.",
    bullets: [
      "notification_preferences row per user with category + delivery_method.",
      "Email templates live in email_templates keyed by event type.",
      "SMTP settings stored in email_settings (single row).",
    ],
  },
  {
    title: "Theming",
    icon: Sun,
    description: "Tokens in src/styles.css, light/dark/system via ThemeProvider.",
    bullets: [
      "All semantic colors defined as oklch CSS variables on :root and .dark.",
      "Use design tokens — never hardcode Tailwind colors in components.",
      "useTheme() exposes theme, resolvedTheme, and setTheme.",
    ],
  },
];

export const helpCategories = [
  {
    title: "For customers",
    description: "Submitting jobs, tracking, favourites, teams, and account settings.",
    icon: Users,
    link: "/help/customers" as const,
    audience: "all" as const,
  },
  {
    title: "Pricing & artwork guidelines",
    description: "Trade pricing per metre, cutting tiers, and shipping options.",
    icon: SettingsIcon,
    link: "/pricing" as const,
    audience: "all" as const,
  },
  {
    title: "For staff",
    description: "Daily workflow, draft/publish, customer view, and admin tools.",
    icon: BookOpen,
    link: "/help/staff" as const,
    audience: "staff" as const,
  },
  {
    title: "Technical docs",
    description: "Architecture, integrations, theming, and notifications internals.",
    icon: FileText,
    link: "/help/docs" as const,
    audience: "staff" as const,
  },
];
